/**
 * Field Mapping Configuration — admin UI to map external column names to internal fields.
 *
 * Sources:
 *   - Core fields list: GET /api/custom-fields/core-fields (registry-driven)
 *   - Custom fields:    via useCustomFields() hook
 *   - Field groups:     via useFieldGroups() hook (children rendered grouped, with badges)
 *   - Mappings:         POST/GET/DELETE /api/custom-fields/mappings
 *   - Bulk import:      POST /api/custom-fields/mappings/bulk (returns created + skipped[reason])
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, AlertCircle, Upload, CheckCircle2 } from 'lucide-react';
import useCustomFields from '@/hooks/useCustomFields';
import useFieldGroups from '@/hooks/useFieldGroups';
import { FieldMappingCreate, membersAPI } from '@/services/api/members';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useTabbedChrome } from '@/components/weavein';

export default function FieldMappingManager() {
  const tabbed = useTabbedChrome();
  const { t } = useAppTranslation();
  const {
    fields,
    mappings,
    loading,
    error,
    createMapping,
    deleteMapping,
    fetchMappings,
  } = useCustomFields();
  const { groups: fieldGroups } = useFieldGroups();

  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedField, setSelectedField] = useState<string>('');
  const [formData, setFormData] = useState<FieldMappingCreate>({
    db_column: '',
    alternative: '',
    source_system: '',
    description: '',
    is_active: true,
  });

  // Core field keys come from the backend registry now (gap 1) — no more
  // hardcoded list that drifts when new core fields are added.
  const [coreFields, setCoreFields] = useState<string[]>([]);
  const [coreFieldsError, setCoreFieldsError] = useState<string | null>(null);

  useEffect(() => {
    fetchMappings();
    membersAPI
      .listMappableCoreFieldKeys()
      .then((keys) => setCoreFields(keys))
      .catch((err) => {
        console.error('Failed to load core fields:', err);
        setCoreFieldsError('Could not load core fields list — using last cached set.');
      });
  }, []);

  const resetForm = () => {
    setFormData({
      db_column: '',
      alternative: '',
      source_system: '',
      description: '',
      is_active: true,
    });
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await createMapping(formData);
    if (success) {
      resetForm();
    }
  };

  const handleDelete = async (mappingId: number, alternativeName: string) => {
    if (window.confirm(`Are you sure you want to delete mapping for "${alternativeName}"?`)) {
      await deleteMapping(mappingId);
    }
  };

  const filterMappings = () => {
    if (!selectedField) return mappings;
    return mappings.filter((m) => m.db_column === selectedField);
  };

  // Field groups: children belong to a group; surface them under the group's
  // friendly name in the dropdown (gap 2). Custom fields that aren't in any
  // group still appear under the "Custom Fields" header below.
  const groupChildIds = useMemo(() => {
    const ids = new Set<number>();
    fieldGroups.forEach((g) => g.children.forEach((c) => ids.add(c.field_id)));
    return ids;
  }, [fieldGroups]);

  const looseCustomFields = useMemo(
    () => fields.filter((f) => !groupChildIds.has(f.id)),
    [fields, groupChildIds]
  );

  const getFieldDisplayName = (fieldName: string): string => {
    const customField = fields.find((f) => f.name === fieldName);
    if (customField) return customField.friendly_name;
    return fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          {!tabbed && (
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
            {t('admin.field_mapping_configuration', 'Field Mapping Configuration')}
          </h2>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t(
              'admin.map_external_field_names_from',
              'Map external field names from CSV/XLS imports to internal database fields'
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBulkImport(true)} size="sm">
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import (CSV)
          </Button>
          <Button variant="primary" onClick={() => setShowAddForm(true)} size="sm">
            <Plus className="w-4 h-4 mr-2" />
            {t('admin.add_mapping', 'Add Mapping')}
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {coreFieldsError && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{coreFieldsError}</AlertDescription>
        </Alert>
      )}

      {/* Add Mapping Dialog */}
      <Dialog open={showAddForm} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('admin.add_field_mapping', 'Add Field Mapping')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="db_column">
                {t('admin.internal_database_field', 'Internal Database Field')} *
              </Label>
              <Select
                value={formData.db_column || undefined}
                onValueChange={(value) => setFormData({ ...formData, db_column: value })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t('admin.select_a_field', 'Select a field...')} />
                </SelectTrigger>
                <SelectContent>
                  <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
                    {t('admin.core_fields', 'Core Fields')} ({coreFields.length})
                  </div>
                  {coreFields.map((field) => (
                    <SelectItem key={`core_${field}`} value={field} className="pl-6">
                      {getFieldDisplayName(field)}
                      <span className="ml-2 text-xs text-gray-400">{field}</span>
                    </SelectItem>
                  ))}

                  {looseCustomFields.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 mt-2">
                        {t('admin.custom_fields', 'Custom Fields')} ({looseCustomFields.length})
                      </div>
                      {looseCustomFields.map((field) => (
                        <SelectItem key={`cf_${field.id}`} value={field.name} className="pl-6">
                          {field.friendly_name}
                          <span className="ml-2 text-xs text-gray-400">{field.name}</span>
                        </SelectItem>
                      ))}
                    </>
                  )}

                  {fieldGroups.map((g) => (
                    <React.Fragment key={`grp_${g.id}`}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-purple-700 dark:text-purple-300 mt-2 flex items-center gap-2">
                        Field Group: {g.friendly_name}
                        {g.is_repeatable && (
                          <span className="text-[10px] px-1 rounded bg-purple-100 text-purple-700">
                            Repeatable
                          </span>
                        )}
                      </div>
                      {g.children.map((c) => (
                        <SelectItem key={`grp_${g.id}_${c.field_id}`} value={c.name} className="pl-6">
                          {c.friendly_name}
                          <span className="ml-2 text-xs text-gray-400">{c.name}</span>
                        </SelectItem>
                      ))}
                    </React.Fragment>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                For repeatable groups, mappings apply to a single child column. Per-instance
                column suffixes (e.g. <code>agency_1_name</code>) are handled at import time;
                map the underlying field once.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="alternative">
                {t('admin.external_field_name', 'External Field Name')} *
              </Label>
              <Input
                id="alternative"
                value={formData.alternative}
                onChange={(e) => setFormData({ ...formData, alternative: e.target.value })}
                placeholder="e.g., MCP_ID, Plan_Member_ID, Member Number"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="source_system">
                {t('admin.source_system_1', 'Source System')}
              </Label>
              <Input
                id="source_system"
                value={formData.source_system}
                onChange={(e) => setFormData({ ...formData, source_system: e.target.value })}
                placeholder="e.g., Health Net, LA Care, State DHCS"
                list="source-systems"
              />
              <datalist id="source-systems">
                <option value="Health Net" />
                <option value="LA Care" />
                <option value="Blue Shield" />
                <option value="State DHCS" />
                <option value="Medicare" />
                <option value="Medi-Cal" />
              </datalist>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                {t('admin.description_1', 'Description')}
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={resetForm}>
                {t('admin.cancel', 'Cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {t('admin.create_mapping', 'Create Mapping')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BulkImportDialog
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        onImported={() => {
          fetchMappings();
        }}
      />

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="w-80">
            <Label htmlFor="filter-field" className="text-sm">
              {t('admin.filter_by_internal_field', 'Filter by internal field')}
            </Label>
            <Select value={selectedField} onValueChange={setSelectedField}>
              <SelectTrigger className="w-full mt-1">
                <SelectValue placeholder={t('admin.all_fields', 'All Fields')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">{t('admin.all_fields', 'All Fields')}</SelectItem>
                {coreFields.map((field) => (
                  <SelectItem key={`fc_${field}`} value={field}>
                    {getFieldDisplayName(field)}
                  </SelectItem>
                ))}
                {fields.map((field) => (
                  <SelectItem key={`ff_${field.id}`} value={field.name}>
                    {field.friendly_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Mappings Table */}
      {loading && mappings.length === 0 ? (
        <div className="w-full mt-8">
          <Progress value={undefined} className="w-full mb-4" />
          <p className="text-center text-gray-600">{t('admin.loading_mappings', 'Loading mappings...')}</p>
        </div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admin.internal_field', 'Internal Field')}</TableHead>
                  <TableHead>{t('admin.external_name', 'External Name')}</TableHead>
                  <TableHead>{t('admin.source_system', 'Source System')}</TableHead>
                  <TableHead>{t('admin.description', 'Description')}</TableHead>
                  <TableHead>{t('admin.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filterMappings().length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <h3 className="text-lg font-semibold mb-2">
                        {t('admin.no_mappings_configured', 'No mappings configured')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Create field mappings to automatically match external field names to
                        internal database fields during data imports.
                      </p>
                      <Button variant="primary" onClick={() => setShowAddForm(true)} size="sm">
                        <Plus className="w-4 h-4 mr-2" />
                        {t('admin.add_first_mapping', 'Add First Mapping')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  filterMappings().map((mapping) => (
                    <TableRow key={mapping.id} className={mapping.is_active ? '' : 'opacity-50'}>
                      <TableCell>
                        <div className="font-semibold text-sm">{getFieldDisplayName(mapping.db_column)}</div>
                        <code className="text-xs text-gray-500">{mapping.db_column}</code>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {mapping.alternative}
                        </code>
                      </TableCell>
                      <TableCell>
                        {mapping.source_system ? (
                          <Badge variant="default">{mapping.source_system}</Badge>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-gray-600">{mapping.description || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(mapping.id, mapping.alternative)}
                          className="border-red-600 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t('admin.delete', 'Delete')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================================
// Bulk Import Dialog (gap 3)
// ============================================================================

interface BulkImportProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

function BulkImportDialog({ open, onClose, onImported }: BulkImportProps) {
  const [csvText, setCsvText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{
    created: number;
    skipped: Array<{ db_column: string; alternative: string; reason: string }>;
  } | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const reset = () => {
    setCsvText('');
    setSubmitting(false);
    setResult(null);
    setParseError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => setCsvText((e.target?.result as string) || '');
    reader.readAsText(file);
  };

  const parseCsv = (text: string): { rows: FieldMappingCreate[]; error: string | null } => {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);
    if (lines.length < 2) {
      return { rows: [], error: 'CSV needs a header row plus at least one data row.' };
    }
    const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
    const idxOf = (name: string) => header.indexOf(name);
    const dbIdx = idxOf('db_column');
    const altIdx = idxOf('alternative');
    if (dbIdx === -1 || altIdx === -1) {
      return {
        rows: [],
        error:
          'Header must include at least "db_column" and "alternative". Optional columns: source_system, description, is_active.',
      };
    }
    const ssIdx = idxOf('source_system');
    const descIdx = idxOf('description');
    const activeIdx = idxOf('is_active');

    const rows: FieldMappingCreate[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(',').map((c) => c.trim());
      const db = cells[dbIdx] || '';
      const alt = cells[altIdx] || '';
      if (!db || !alt) continue;
      rows.push({
        db_column: db,
        alternative: alt,
        source_system: ssIdx >= 0 ? cells[ssIdx] || undefined : undefined,
        description: descIdx >= 0 ? cells[descIdx] || undefined : undefined,
        is_active: activeIdx >= 0 ? !/^(false|0|no)$/i.test(cells[activeIdx]) : true,
      });
    }
    return { rows, error: null };
  };

  const handleSubmit = async () => {
    setParseError(null);
    setResult(null);
    const { rows, error: pErr } = parseCsv(csvText);
    if (pErr) {
      setParseError(pErr);
      return;
    }
    if (!rows.length) {
      setParseError('No rows to import.');
      return;
    }
    setSubmitting(true);
    try {
      const resp = await membersAPI.bulkCreateFieldMappings(rows);
      setResult({ created: resp.created.length, skipped: resp.skipped });
      if (resp.created.length > 0) onImported();
    } catch (e: any) {
      setParseError(e?.response?.data?.detail || e?.message || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Bulk Import Field Mappings</DialogTitle>
          <DialogDescription>
            Paste a CSV or upload a file. Required columns:{' '}
            <code>db_column,alternative</code>. Optional:{' '}
            <code>source_system,description,is_active</code>.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-3">
            <Input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
            <div>
              <Label htmlFor="csv-text" className="text-sm">
                Or paste CSV here
              </Label>
              <Textarea
                id="csv-text"
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                rows={10}
                placeholder={
                  'db_column,alternative,source_system\nfirst_name,FNAME,Health Net\nemail,Email_Address,Health Net\n'
                }
                className="font-mono text-xs"
              />
            </div>
            {parseError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{parseError}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Imported {result.created} mapping{result.created === 1 ? '' : 's'}.
                {result.skipped.length > 0 && ` Skipped ${result.skipped.length}.`}
              </AlertDescription>
            </Alert>
            {result.skipped.length > 0 && (
              <div className="border rounded p-3 max-h-48 overflow-y-auto text-xs space-y-1">
                <div className="font-semibold mb-1">Skipped rows</div>
                {result.skipped.map((s, idx) => (
                  <div key={idx}>
                    <code className="text-gray-700">
                      {s.db_column} ← {s.alternative}
                    </code>{' '}
                    <span className="text-red-600">— {s.reason}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button variant="primary" onClick={close}>
              Done
            </Button>
          ) : (
            <>
              <Button variant="secondary" onClick={close}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit} disabled={submitting || !csvText.trim()}>
                {submitting ? 'Importing…' : 'Import'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
